import { useTileActive } from '@components/shared/tile-context';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenu as RadixDropdownMenu,
} from '@components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import * as React from 'react';

export type DropdownMenuItemType = {
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  tooltip?: string;
  text: string;
  variant?: 'default' | 'danger';
};

type DropdownMenuProps = {
  /**
   * Alignment of the dropdown content relative to the trigger.
   * Defaults to 'end' (right-aligned).
   */
  align?: 'start' | 'center' | 'end';
  className?: string;
  header?: React.ReactNode;
  items: DropdownMenuItemType[];
  /**
   * Callback when the dropdown open state changes.
   * Useful for keeping trigger buttons visible while dropdown is open.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Which side of the trigger to render the dropdown.
   * Defaults to 'bottom'.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Offset from the trigger (in pixels).
   * Defaults to 4.
   */
  sideOffset?: number;
  trigger: React.ReactNode;
};

export const DropdownMenuComponent: React.FC<DropdownMenuProps> = (props) => {
  const { align = 'end', className, header, items, onOpenChange, side = 'bottom', sideOffset = 4, trigger } = props;

  // Automatically register with Tile context to keep hover-reveal elements visible
  const setTileActive = useTileActive();

  const handleOpenChange = (open: boolean): void => {
    setTileActive(open);
    onOpenChange?.(open);
  };

  const getItemClassName = (item: DropdownMenuItemType) => {
    return cn(
      item.className,
      item.variant === 'danger' && 'text-red-500 hover:bg-red-50! hover:text-red-500!',
      item.variant === 'default' && 'hover:bg-gray-100',
      !item.disabled && !item.onClick && 'cursor-default',
      !item.disabled && item.onClick && 'cursor-pointer',
    );
  };

  return (
    <RadixDropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={className}
        side={side}
        sideOffset={sideOffset}
      >
        {header && (
          <>
            <div className="px-2 py-1.5">{header}</div>
            <DropdownMenuSeparator />
          </>
        )}
        {items.map((item, index) => {
          const menuItem = (
            <DropdownMenuItem
              className={getItemClassName(item)}
              disabled={item.disabled}
              onClick={item.onClick}
            >
              {item.text}
            </DropdownMenuItem>
          );

          if (item.disabled && item.tooltip) {
            return (
              <TooltipProvider
                key={`${item.text}-${index}`}
                delayDuration={0}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">{menuItem}</div>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return <React.Fragment key={`${item.text}-${index}`}>{menuItem}</React.Fragment>;
        })}
      </DropdownMenuContent>
    </RadixDropdownMenu>
  );
};
